import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { Box, Text, Badge, HStack, VStack, Icon, Spinner, Flex, Separator } from "@chakra-ui/react";
import axiosInstance from "../services/api";
import { formatTime } from "../store/helpers";
import dayjs from "dayjs";
import _ from "lodash";


function TicketHistoryCard({
  ticket,
}) {
  const [ticketHistory, setTicketHistory] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Helper function to get full name from user ID
  const getFullName = (userId) => {
    if (!userId) return "Unknown";
    const user = users.find(user => user?.id === userId);
    if (!user) return "Unknown";
    return `${user.name || ''} ${user.lastname || ''}`.trim() || "Unknown";
  };

  useEffect(() => {
    // Reset history when ticket changes
    if (ticket?.id !== prevTicketId.current) {
      setTicketHistory([]);
      prevTicketId.current = ticket?.id;
    }
  }, [ticket]);
  
  // Use a ref to track the previous ticket ID
  const prevTicketId = useRef(null);
  
  // Separate useEffect for fetching data to avoid dependency issues
  useEffect(() => {
    const ticketId = ticket?.id;
    const organizationId = ticket?.organizationId;
    const branchId = ticket?.branchId;
    const created = ticket?.created;
    
    // Only fetch if we have all required data and no existing history
    if (!ticketId || !organizationId || !branchId || !created || ticketHistory.length > 0) {
      return;
    }
    
    // Use a flag to prevent state updates after unmount
    let isMounted = true;
    
    const fetchTicketHistory = async () => {
      setLoading(true);
      
      try {
        const [transferResponse, callResponse, usersResponse] = await Promise.all([
          axiosInstance.get(`/management/transferTickets/byTicketId`, {
            params: { ticketId, organizationId, branchId, created }
          }),
          axiosInstance.get(`/management/callTickets/byTicketId`, {
            params: { ticketId, organizationId, branchId, created }
          }),
          axiosInstance.get(`/iam/users`, {
            params: { organizationId }
          })
        ]);
        
        if (!isMounted) return;
        
        // Add historyType field to each item in the arrays
        const transferTicketsWithType = (transferResponse.data || []).map(item => ({
          ...item,
          historyType: 'transfer'
        }));
        
        const callTicketsWithType = (callResponse.data || []).map(item => ({
          ...item,
          historyType: 'call'
        }));
        
        // Combine both arrays
        const allTickets = [...transferTicketsWithType, ...callTicketsWithType];
        
        // Remove duplicates based on id and created timestamp
        const seen = new Map();
        const uniqueTickets = allTickets.filter(item => {
          if (!item) return false;
          // Create a unique key based on multiple properties
          const key = `${item.id || ''}-${item.created || ''}-${item.historyType || ''}`;
          
          // If we haven't seen this item before, keep it
          if (!seen.has(key)) {
            seen.set(key, true);
            return true;
          }
          
          // Otherwise, it's a duplicate, so filter it out
          return false;
        });
        
        // Sort by created timestamp (oldest first)
        const sortedHistory = uniqueTickets.sort((a, b) => (a.created || 0) - (b.created || 0));
        
        setTicketHistory(sortedHistory);
        setUsers(usersResponse.data || []);
      } catch (error) {
        console.error("Error fetching ticket data:", error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchTicketHistory();
    
    // Cleanup function to prevent memory leaks
    return () => {
      isMounted = false;
    };
  }, [ticket, ticketHistory.length]);

  return (
    <Box mt={"20px"} p={4} borderWidth="1px" borderRadius="lg" w={"100%"}>
      <Text fontSize={16} fontWeight={600} mb={4}>Ticket History</Text>

      {
        loading ? (
          <HStack>
            <Spinner size="sm" color="blue.500" />
            <Text>Loading Ticket history...</Text>
          </HStack>
        ) : (
          <VStack align="stretch" gap={3} w="100%">
            {ticketHistory.length > 0 ? (
              ticketHistory.map((item, index) => (
                <Box 
                  key={`${item.id}-${item.created}-${item.historyType}`} 
                  p={3} 
                  bg={item.historyType === 'transfer' ? 'blue.50' : 'green.50'}
                  borderRadius="md"
                  borderLeft={`4px solid ${item.historyType === 'transfer' ? 'blue.500' : 'green.500'}`}
                >
                  <HStack justifyContent="space-between" mb={2}>
                    <Badge colorScheme={item.historyType === 'transfer' ? 'blue' : 'green'}>
                      {item.historyType === 'transfer' ? 'Ticket Transferred' : 'Ticket Called'}
                    </Badge>
                  </HStack>
                  
                  {item.historyType === 'transfer' ? (
                    <VStack align="start">
                      <HStack>
                        <Text fontSize="14px" w="110px" fontWeight={500}>
                        Transferred By:
                        </Text>
                        <Text fontSize="14px" fontWeight={400} color="var(--black-dry)">
                        {getFullName(item.transferAgent || '')} via <strong>{(item.transferType || '').toUpperCase()} Transfer</strong>
                        </Text>
                      </HStack>
                      <HStack>
                        <Text fontSize="14px" w="110px" fontWeight={500}>
                        To: 
                        </Text>
                        <Text fontSize="14px" fontWeight={400} color="var(--black-dry)">
                        {
                        item.transferType === 'agent'
                          ? getFullName(item.ticket?.manage?.userId || '')
                          : <>
                            {
                              item.ticket?.manage?.supportDesk
                                ? `${item.ticket?.manage?.supportDesk?.type || ''} ${item.ticket?.manage?.supportDesk?.number || ''}`
                                : "Unknown"
                            }
                          </>
                      }
                        </Text>
                      </HStack>
                      {/* nampost tracking information */}
                      {!_.isEmpty(item.ticket?.tracking) ? (
                        <Box mt="10px">
                          {/* title */}
                          <Text fontSize="14px" fontWeight={600}>
                            Tracking Information at Transfer
                          </Text>
                          <HStack>
                            <Text fontSize="14px" w="110px" fontWeight={500}>
                              Status:
                            </Text>
                            <Text fontSize="14px" fontWeight={400} color="var(--black-dry)">
                              {item.ticket?.tracking?.packageStatus || 'Unknown'}
                            </Text>
                          </HStack>
                          {!_.isEmpty(item.ticket?.tracking?.referenceNumber) ? (<HStack>
                            <Text fontSize="14px" w="110px" fontWeight={500}>
                              Reference #:
                            </Text>
                            <Text fontSize="14px" fontWeight={400} color="var(--black-dry)">
                              {item.ticket?.tracking?.referenceNumber}
                            </Text>
                          </HStack>) : null}
                          {!_.isEmpty(item.ticket?.tracking?.trackingNumber) ? (<HStack>
                            <Text fontSize="14px" w="110px" fontWeight={500}>
                              Tracking #:
                            </Text>
                            <Text fontSize="14px" fontWeight={400} color="var(--black-dry)">
                              {item.ticket?.tracking?.trackingNumber}
                            </Text>
                          </HStack>) : null}
                          {!_.isEmpty(item.ticket?.tracking?.packageNotes) ? (<HStack>
                            <Text fontSize="14px" w="110px" fontWeight={500}>
                              Notes:
                            </Text>
                            <Text fontSize="14px" fontWeight={400} color="var(--black-dry)">
                              {item.ticket?.tracking?.packageNotes}
                            </Text>
                          </HStack>) : null}
                        </Box>
                      ) : null}
                      <Text>Transferred At: {item.created ? dayjs.unix(item.created).format("D MMM, YY HH:mm:ss") : 'Unknown'}</Text>
                    </VStack>
                  ) : (
                    <VStack align="start">
                      <HStack> 
                        <Text fontSize="14px" w="110px" fontWeight={500}>
                        Agent:
                        </Text>
                        <Text fontSize="14px" fontWeight={400} color="var(--black-dry)">
                        {getFullName(item.manage?.userId || '')}
                        </Text>
                      </HStack>
                      <HStack> 
                        <Text fontSize="14px" w="110px" fontWeight={500}>
                        Counter:
                        </Text>
                        <Text fontSize="14px" fontWeight={400} color="var(--black-dry)">
                        {item.manage?.supportDesk?.type || ''} {item.manage?.supportDesk?.number || ''}
                        </Text>
                      </HStack>
                      {/* nampost tracking information */}
                      {!_.isEmpty(item?.tracking) ? (
                        <Box mt="10px">
                          {/* title */}
                          <Text fontSize="14px" fontWeight={600}>
                            Tracking Information at Transfer
                          </Text>
                          <HStack>
                            <Text fontSize="14px" w="110px" fontWeight={500}>
                              Status:
                            </Text>
                            <Text fontSize="14px" fontWeight={400} color="var(--black-dry)">
                              {item?.tracking?.packageStatus || 'Unknown'}
                            </Text>
                          </HStack>
                          {!_.isEmpty(item?.tracking?.referenceNumber) ? (<HStack>
                            <Text fontSize="14px" w="110px" fontWeight={500}>
                              Reference #:
                            </Text>
                            <Text fontSize="14px" fontWeight={400} color="var(--black-dry)">
                              {item?.tracking?.referenceNumber}
                            </Text>
                          </HStack>) : null}
                          {!_.isEmpty(item?.tracking?.trackingNumber) ? (<HStack>
                            <Text fontSize="14px" w="110px" fontWeight={500}>
                              Tracking #:
                            </Text>
                            <Text fontSize="14px" fontWeight={400} color="var(--black-dry)">
                              {item?.tracking?.trackingNumber}
                            </Text>
                          </HStack>) : null}
                          {!_.isEmpty(item?.tracking?.packageNotes) ? (<HStack>
                            <Text fontSize="14px" w="110px" fontWeight={500}>
                              Notes:
                            </Text>
                            <Text fontSize="14px" fontWeight={400} color="var(--black-dry)">
                              {item?.tracking?.packageNotes}
                            </Text>
                          </HStack>) : null}
                        </Box>
                      ) : null}
                      <HStack> 
                        <Text fontSize="14px" w="110px" fontWeight={500}>
                        Called At:
                        </Text>
                        <Text fontSize="14px" fontWeight={400} color="var(--black-dry)">
                        {item.created ? dayjs.unix(item.created).format("D MMM, YY HH:mm:ss") : 'Unknown'}
                        </Text>
                      </HStack>
                    </VStack>
                  )}
                </Box>
              ))
            ) : (
              <Text color="gray.500">No ticket history found</Text>
            )}
          </VStack>
        )
      }
    </Box>
  );
}

TicketHistoryCard.propTypes = {
  ticket: PropTypes.shape({
    id: PropTypes.string.isRequired,
    organizationId: PropTypes.string.isRequired,
    branchId: PropTypes.string.isRequired,
  }).isRequired
};

export default TicketHistoryCard;
